
import { useCallback, useMemo } from 'react';

import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Tooltip } from 'antd';
import { connect } from 'react-redux';

import { IAccount, IRating } from '@share/common-types';
import { DATE_FORMAT_CHECK_IN_OUT_YEAR, MINIMUM_RATING_TO_SHOW, NOT_IMAGE_PROPERTY } from '@constants';
import { getCondoTextRating } from '@utils';
import { getReserveUnitUrl, getSelectedCurrency, to12Hour } from '@share/utils';
import {
  creatingArrayWithNumber,
  formatDateCheckInOut,
  getRankingClass,
  renderNumber,
  RootState,
} from '@share/utils';
import { InfoSvg, ManDarkSvg, NoImageSvg } from '@assets';
import { BlueButton } from '@share/components';
import { ILoginState } from '@share/store/slices';
import { Currency, HotelStars } from '@components';

import { RefundableEnum } from '@common-types';

import './style.scss';
import { getAccountDateFnsLocale } from '@i18n';

interface IMapStateToProps {
  loginStore: ILoginState;
}

interface IProps extends IMapStateToProps {
  imageUrl: string;
  data?: {
    title: string;
    address: string;
    unitName: string;
    unitDescription?: string;
    maxOccupancy?: number;
    adultsCount: number;
    childrenCount: number;
    bedroomsCount: number;
    bathroomsCount: number;
    checkIn: string;
    checkInTime?: string | null | undefined;
    checkOut: string;
    checkOutTime?: string | null | undefined;
    nightsCount: number;
    condoId?: number;
    unitId?: string;
    price?: number;
    isNotification?: boolean;
    bookingComplete?: boolean;
    isModalInfo?: boolean;
    rating?: IRating;
    supplierIdentifier?: string;
    walletPrices?: any;
    refundability?: string;
  }
  isAllInclusive?: boolean;
  savingsPercent?: number;
  hideImage?: boolean;
  renderOnlyImage?: boolean;
  // When provided, the modal "Reserve" action calls this instead of navigating via a plain <Link>.
  // Callers use it to seed the review client cash (points selected in search) before navigating,
  // so the value carries into checkout — matching the row-level reserve handlers.
  onReserve?: () => void;
}

const ZERO = 0;
const ONE = 1;

function UnitMainInfoComponent(props: IProps) {

  const { imageUrl, data, hideImage, renderOnlyImage, isAllInclusive, savingsPercent, loginStore, onReserve } = props;
  const { account } = loginStore;

  const refundability = data?.refundability;
  const title = data?.title;
  const address = data?.address;
  const unitDescription = data?.unitDescription;
  const maxOccupancy = data?.maxOccupancy;
  const checkIn = data?.checkIn;
  const checkOut = data?.checkOut;
  const nightsCount = data?.nightsCount;
  const condoId = data?.condoId;
  const unitId = data?.unitId;
  const price = data?.price;
  const checkInTime = data?.checkInTime;
  const checkOutTime = data?.checkOutTime;
  const isModalInfo = data?.isModalInfo;
  const rating = data?.rating;
  const supplierIdentifier = data?.supplierIdentifier;
  const unitName = data?.unitName;
  const walletPrices = data?.walletPrices;

  const isRefundable = refundability === RefundableEnum.Refundable;

  const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

  const hasNotRealImageProperty = (): boolean => {
    return !!(imageUrl && imageUrl?.toLowerCase()?.includes(NOT_IMAGE_PROPERTY));
  };

  const renderNotRealImageProperty = () => {
    return (
      <div style={{ position: 'absolute', top: '0', right: '0', bottom: '0', left: '0', background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
        <p className="condo-hotel-card__label-title" style={{ color: 'white', fontSize: '12px' }}>
          <FormattedMessage
            id="not.real.image.property"
            defaultMessage="Not actual photo of the property"
          />
        </p>
      </div>
    );
  };

  const renderImage = () => {
    if (hideImage && !renderOnlyImage) {
      return null;
    }

    const notRealImageProperty = hasNotRealImageProperty();
    const styleImg: any = {};
    if (notRealImageProperty) {
      styleImg.filter = 'grayscale(100%)';
    }

    return imageUrl?.length !== ZERO ? (
      <div className="unit-main-info__unit-image print">
        {notRealImageProperty && renderNotRealImageProperty()}

        <img className="unit-main-info__unit-img print" src={imageUrl} alt={unitName} style={styleImg} />

        {isAllInclusive ? (
          <div className="condo-hotel-card__label-all-inclusive">
            <p className="condo-hotel-card__label-title">
              <FormattedMessage id="all.inclusive" />
            </p>
          </div>) : null}

        {(savingsPercent && savingsPercent > ZERO && !displayWalletSavings) ? (
          <div className={`condo-hotel-card__label ${isAllInclusive ? 'two-label' : ''}`}>
            <p className="condo-hotel-card__label-title">
              <FormattedMessage id="save" /> {savingsPercent}%
            </p>
          </div>) : null}

        {displayWalletSavings ? (
          <div className={`condo-hotel-card__label ${isAllInclusive ? 'two-label' : ''}`}>
            <p className="condo-hotel-card__label-title">
              <FormattedMessage id="save.up.to" values={{ number: walletPrices?.priceSavings ? walletPrices?.priceSavings : '<1' }} />
            </p>
          </div>) : null}
      </div>) : (
      <div className="unit-info__unit-img no-image">
        <NoImageSvg />

        {isAllInclusive ? (
           <div className="condo-hotel-card__label-all-inclusive">
             <p className="condo-hotel-card__label-title">
               <FormattedMessage id="all.inclusive" />
             </p>
           </div>) : null}

        {(savingsPercent && savingsPercent > ZERO && !displayWalletSavings) ? (
           <div className={`condo-hotel-card__label ${isAllInclusive ? 'two-label' : ''}`}>
             <p className="condo-hotel-card__label-title">
               <FormattedMessage id="save" /> {savingsPercent}%
             </p>
           </div>) : null}

        {displayWalletSavings ? (
           <div className={`condo-hotel-card__label ${isAllInclusive ? 'two-label' : ''}`}>
             <p className="condo-hotel-card__label-title">
               <FormattedMessage id="save.up.to" values={{ number: walletPrices?.priceSavings ? walletPrices?.priceSavings : '<1' }} />
             </p>
           </div>) : null}

        {notRealImageProperty && renderNotRealImageProperty()}
      </div>);
  }

  const renderRefundability = useCallback(() => {
    return (
      <div className="unit-main-info__refundable" style={{ display: 'inline-block' }}>
        <div className={isRefundable ? 'unit-main-info__refundable-ref' : 'unit-main-info__refundable-nonref'} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            <FormattedMessage id={isRefundable ? 'refundable' : 'non.refundable'} />
          </span>
          <InfoSvg />
        </div>
      </div>);
  }, [isRefundable]);
  
  const showRating = rating && rating?.value > ZERO;
  
  const locale = getAccountDateFnsLocale(account);

  const currency = useMemo(() => getSelectedCurrency(account as IAccount), [account]);
  const rankingClass = useMemo(() => getRankingClass(rating?.value as number), [rating?.value]);

  return (
    <div className="unit-main-info">
      {renderImage()}
      {!renderOnlyImage ? (
        <div className="unit-main-info__content">
          {supplierIdentifier ? 
            <p className="unit-main-info__supplier"><FormattedMessage id={`suppliers.${supplierIdentifier}`} /></p>: null}
          <p className="unit-main-info__condo-name secondary-font">{title}</p>
          <p className="unit-main-info__address">{address}</p>
          {!isModalInfo ? (

            <div className="unit-main-info__link-reviews">
              {showRating ? <HotelStars stars={1} /> : null}
              <span className={`unit-main-info__trust-you-text ${rankingClass}`} style={{ marginLeft: '5px' }}>
                {showRating ? renderNumber(rating.value, ONE) : ''}
              </span>
              {rating?.reviewsCount ? (
                <p className="unit-main-info__trust-you-text" style={{ paddingTop: '4px' }}>
                  ({renderNumber(rating.reviewsCount)} <FormattedMessage id="reviews" />)
                </p>
              ) : showRating && rating.value >= MINIMUM_RATING_TO_SHOW ? (
                <p className="unit-main-info__trust-you-text" style={{ paddingTop: '4px' }}>
                  <FormattedMessage
                    id={getCondoTextRating(Number(renderNumber(rating.value, ONE)))}
                  />
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="unit-main-info__unit-info">
            <div className="unit-main-info__unit-name-wrapper">
              <Tooltip title={unitName} placement="topLeft">
                <p className="unit-main-info__unit-name capitalize">{unitName}</p>
              </Tooltip>
              {(maxOccupancy && maxOccupancy > ZERO) && (
                <div className="unit-main-info__guests">
                  {creatingArrayWithNumber(maxOccupancy, ONE).map((_value, i) => {
                    return (
                      <span className="unit-main-info__icon-wrapper" key={i}>
                        <ManDarkSvg />
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="unit-main-info__unit-info-description lowercase">{unitDescription}</p>
          </div>

          <div className="unit-main-info__period">
            {checkIn && checkOut && (
              <>
                <div className="unit-main-info__period-item">
                  <p className="unit-main-info__period-item-title">
                    <FormattedMessage id="check.in" />:
                  </p>
                  <p className="unit-main-info__period-item-description">
                    {formatDateCheckInOut(checkIn, DATE_FORMAT_CHECK_IN_OUT_YEAR, locale)}
                    {checkInTime ? (
                      <>
                        {', '}
                        {to12Hour(checkInTime)}
                      </>
                    ) : null}
                  </p>
                </div>
                <div className="unit-main-info__period-item">
                  <p className="unit-main-info__period-item-title">
                    <FormattedMessage id="check.out" />:
                  </p>
                  <p className="unit-main-info__period-item-description">
                    {formatDateCheckInOut(checkOut, DATE_FORMAT_CHECK_IN_OUT_YEAR, locale)}
                    {checkOutTime ? (
                      <>
                        {', '}
                        <FormattedMessage id="till" /> {to12Hour(checkOutTime)}
                      </>
                    ) : null}
                  </p>
                </div>
              </>)}
              <div className="unit-main-info__period-item">
                <p className="unit-main-info__period-item-title">
                  <FormattedMessage id="nigths" />:
                </p>
                <p className="unit-main-info__period-item-description">
                  <FormattedMessage id="nights" values={{ count: nightsCount }} />
                </p>
              </div>
          </div>
          
          {(price !== ZERO && isModalInfo) ? (
            <div className="unit-main-info__price-block">
              <div className="unit-main-info__price-total">
                {refundability && renderRefundability()}

                <p className="unit-main-info__price-total-text">
                  <Currency currency={currency} />{renderNumber(price as number, 2)}
                </p>

                <div className="unit-main-info__for-info">
                  <span className="unit-main-info__price-includes-taxes">
                    <FormattedMessage id="abbreviated.price.includes.taxes" />
                  </span>
                </div>
              </div>

              <div className="unit-main-info__action">
                {onReserve ? (
                  <BlueButton onClick={onReserve}>
                    <FormattedMessage id="reserve" />
                  </BlueButton>
                ) : (
                  <Link to={getReserveUnitUrl(account?.name as string, condoId as number, unitId as string, false)}>
                    <BlueButton>
                      <FormattedMessage id="reserve" />
                    </BlueButton>
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="unit-main-info__price-block">
              <div className="unit-main-info__price-total">
                {refundability && renderRefundability()}
              </div>
            </div>
          )}
        </div>) : null }
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore
  };
};

export const UnitMainInfo = connect(mapStateToProps)(UnitMainInfoComponent);

